import { z } from "zod"

import { entityIdSchema, platformOperatorIdSchema } from "./identifiers.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)

export const platformAnalyticsSectionV1Schema = z.enum([
  "tenant_lifecycle",
  "revenue_collections",
  "adoption_aggregate",
  "support_sla",
  "reliability_slo",
  "capacity",
  "backup_migration",
  "security_access",
  "release_progress",
])

export const platformAnalyticsQueryV1Schema = z.object({
  platformOperatorId: platformOperatorIdSchema,
  sections: z.array(platformAnalyticsSectionV1Schema).min(1).max(9),
  bucket: z.enum(["day", "week", "month"]),
  from: timestampSchema,
  to: timestampSchema,
  asOf: timestampSchema,
  limit: z.number().int().min(1).max(200).default(50),
  cursor: entityIdSchema.nullable().default(null),
}).strict().superRefine((query, context) => {
  const duration = Date.parse(query.to) - Date.parse(query.from)
  if (duration < 0 || duration > 366 * 24 * 60 * 60 * 1_000) {
    context.addIssue({ code: "custom", message: "Analytics window must be at most 366 days." })
  }
  if (new Set(query.sections).size !== query.sections.length) {
    context.addIssue({ code: "custom", message: "Analytics sections must be unique." })
  }
})

const dimensionSchema = z.record(codeSchema, codeSchema)
  .refine((value) => Object.keys(value).length <= 8)
const measureSchema = z.record(
  codeSchema,
  z.number().finite().min(-1_000_000_000_000).max(1_000_000_000_000),
).refine((value) => Object.keys(value).length <= 20)

export const platformAnalyticsBucketV1Schema = z.object({
  analyticsBucketId: entityIdSchema,
  section: platformAnalyticsSectionV1Schema,
  bucketStart: timestampSchema,
  bucketEnd: timestampSchema,
  dimensions: dimensionSchema,
  measures: measureSchema,
  sampleSize: z.number().int().nonnegative(),
  suppressedForPrivacy: z.boolean(),
  generatedAt: timestampSchema,
  safeAggregateOnly: z.literal(true),
}).strict()

export const platformAnalyticsPageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: timestampSchema,
  items: z.array(platformAnalyticsBucketV1Schema).max(200),
  nextCursor: entityIdSchema.nullable(),
  partial: z.boolean(),
  freshness: z.enum(["current", "stale", "degraded"]),
  safeAggregateOnly: z.literal(true),
}).strict()

export const requestPlatformAnalyticsExportV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema,
  request: z.object({
    analyticsExportId: entityIdSchema,
    sections: z.array(platformAnalyticsSectionV1Schema).min(1).max(9),
    bucket: z.enum(["day", "week", "month"]),
    from: timestampSchema,
    to: timestampSchema,
    format: z.enum(["csv", "json"]),
    maximumRows: z.number().int().min(1).max(10_000),
    purposeCode: codeSchema,
  }).strict(),
}).strict().superRefine((command, context) => {
  if (
    command.envelope.target.scope !== "platform"
    || command.envelope.target.targetType !== "platform_analytics_export"
    || command.envelope.target.targetId !== command.request.analyticsExportId
    || command.envelope.target.expectedVersion !== 0
    || command.envelope.requiredApprovalIds.length !== 1
  ) {
    context.addIssue({ code: "custom", message: "Analytics export requires one approved Platform command." })
  }
  const duration = Date.parse(command.request.to) - Date.parse(command.request.from)
  if (duration < 0 || duration > 366 * 24 * 60 * 60 * 1_000) {
    context.addIssue({ code: "custom", message: "Export window must be at most 366 days." })
  }
})

export const platformAnalyticsExportReceiptV1Schema = z.object({
  responseVersion: z.literal(1),
  analyticsExportId: entityIdSchema,
  state: z.literal("queued"),
  maximumRows: z.number().int().min(1).max(10_000),
  requestedByPlatformOperatorId: platformOperatorIdSchema,
  requestedAt: timestampSchema,
  expiresAt: timestampSchema,
  objectReference: z.null(),
  safeAggregateOnly: z.literal(true),
}).strict()

export type PlatformAnalyticsQueryV1 = z.infer<typeof platformAnalyticsQueryV1Schema>
export type PlatformAnalyticsBucketV1 = z.infer<typeof platformAnalyticsBucketV1Schema>
export type PlatformAnalyticsPageV1 = z.infer<typeof platformAnalyticsPageV1Schema>
export type RequestPlatformAnalyticsExportV1 = z.infer<
  typeof requestPlatformAnalyticsExportV1Schema
>
export type PlatformAnalyticsExportReceiptV1 = z.infer<
  typeof platformAnalyticsExportReceiptV1Schema
>
